import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconButton, Tooltip } from '@material-ui/core';
import CategoryOutlinedIcon from '@material-ui/icons/CategoryOutlined';
import CloseIcon from '@material-ui/icons/Close';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import RestoreIcon from '@material-ui/icons/Restore';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import orderedCollectionStyles from './orderedCollectionStyles';
import AutoFillControl from './AutoFillControl';
import PinAction from './PinAction';
import useOrderedCollectionMotion from './useOrderedCollectionMotion';

const MAX_VISIBLE_CATEGORIES = 6;

const S = {
  ...orderedCollectionStyles,
  searchArea: {
    position: 'relative',
    padding: '12px 20px',
    borderBottom: '1px solid #edf2f0',
    background: '#fbfefd',
  },
  searchInput: {
    width: '100%',
    minHeight: 40,
    boxSizing: 'border-box',
    padding: '8px 12px',
    border: '1px solid #d5e1dd',
    borderRadius: 8,
    background: '#fff',
    color: '#1e2248',
    fontSize: 13,
  },
  dropdown: {
    position: 'absolute',
    zIndex: 100,
    top: 'calc(100% - 8px)',
    right: 20,
    left: 20,
    maxHeight: 320,
    overflowY: 'auto',
    border: '1px solid #ddeae5',
    borderRadius: 8,
    background: '#fff',
    boxShadow: '0 12px 28px rgba(30, 34, 72, 0.14)',
  },
  dropdownItem: {
    display: 'grid',
    minHeight: 58,
    gridTemplateColumns: '36px minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    borderBottom: '1px solid #edf2f0',
    cursor: 'pointer',
  },
  dropdownEmpty: { padding: 16, color: '#66708a', fontSize: 13, textAlign: 'center' },
  row: {
    ...orderedCollectionStyles.row,
    gridTemplateColumns: '32px 40px minmax(0, 1fr) auto 36px 36px',
  },
  thumb: {
    width: 40,
    height: 40,
    borderRadius: 6,
    objectFit: 'cover',
    background: '#f1f3f6',
  },
  thumbEmpty: {
    display: 'flex',
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    background: '#f1f3f6',
    color: '#8790a5',
  },
  count: {
    color: '#66708a',
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  categoryId: { marginLeft: 6, color: '#8790a5', fontSize: 11, fontWeight: 400 },
  addButton: {
    ...orderedCollectionStyles.addButton,
    minHeight: 32,
    padding: '0 12px',
  },
};

const CategoryThumb = ({ category, small = false }) => {
  const sizeStyle = small ? { width: 36, height: 36 } : null;
  if (category.image_url) {
    return <img src={category.image_url} alt={category.name} style={{ ...S.thumb, ...sizeStyle }} />;
  }
  return (
    <div style={{ ...S.thumbEmpty, ...sizeStyle }} aria-hidden="true">
      <CategoryOutlinedIcon style={{ fontSize: 19 }} />
    </div>
  );
};

const CategoriesEditor = ({
  pinnedCategories = [],
  resolvedCategories = [],
  excludedCategoryIds = [],
  generatedCategories = [],
  onSearch,
  onChange,
  onExcludedChange = () => {},
  autoFill = true,
  onAutoFillChange = () => {},
  hideHeader = false,
}) => {
  const t = useLocalizedMessage();
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  const excludedIds = useMemo(
    () => new Set(excludedCategoryIds.map((id) => `${id}`)),
    [excludedCategoryIds],
  );
  const resolvedById = useMemo(() => new Map(
    resolvedCategories.map(category => [`${category.id}`, category]),
  ), [resolvedCategories]);
  const hydratedGeneratedCategories = useMemo(() => generatedCategories.map((category) => ({
    ...category,
    ...(resolvedById.get(`${category.id}`) || {}),
  })), [generatedCategories, resolvedById]);

  const effectiveItems = useMemo(() => {
    const seen = new Set();
    const manual = [];
    pinnedCategories.forEach((category) => {
      const key = `${category.id}`;
      if (seen.has(key) || manual.length >= MAX_VISIBLE_CATEGORIES) return;
      manual.push({ ...category, key, manual: true });
      seen.add(key);
    });

    const automatic = [];
    if (autoFill) hydratedGeneratedCategories.forEach((category) => {
      const key = `${category.id}`;
      if (
        seen.has(key)
        || excludedIds.has(key)
        || manual.length + automatic.length >= MAX_VISIBLE_CATEGORIES
      ) return;
      automatic.push({ ...category, key, manual: false });
      seen.add(key);
    });
    return [...manual, ...automatic];
  }, [autoFill, excludedIds, hydratedGeneratedCategories, pinnedCategories]);
  const { containerRef: listMotionRef, animateNextChange } = useOrderedCollectionMotion(
    effectiveItems.map(category => category.key),
  );

  const initialSuggestions = useMemo(() => (
    hydratedGeneratedCategories
      .filter((category) => (
        !effectiveItems.some((item) => item.key === `${category.id}`)
        && !excludedIds.has(`${category.id}`)
      ))
      .slice(0, 8)
  ), [effectiveItems, excludedIds, hydratedGeneratedCategories]);

  const knownCategories = useMemo(() => {
    const byId = new Map();
    [...generatedCategories, ...pinnedCategories, ...resolvedCategories, ...searchResults].forEach((category) => {
      byId.set(`${category.id}`, category);
    });
    return byId;
  }, [generatedCategories, pinnedCategories, resolvedCategories, searchResults]);

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (
        dropdownRef.current
        && !dropdownRef.current.contains(event.target)
        && searchRef.current
        && !searchRef.current.contains(event.target)
      ) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const addCategory = useCallback((category) => {
    const key = `${category.id}`;
    if (effectiveItems.some((item) => item.key === key)) return;
    if (excludedIds.has(key)) {
      onExcludedChange(excludedCategoryIds.filter((id) => `${id}` !== key));
    }
    onChange([...pinnedCategories, category]);
    setSearchInput('');
    setSearchResults([]);
    setShowDropdown(false);
  }, [effectiveItems, excludedCategoryIds, excludedIds, onChange, onExcludedChange, pinnedCategories]);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSearchResults(initialSuggestions);
      setShowDropdown(initialSuggestions.length > 0);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await onSearch(value.trim());
        setSearchResults(results || []);
        setShowDropdown(true);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, [initialSuggestions, onSearch]);

  const handleDragEnd = useCallback((result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    const moved = effectiveItems[result.source.index];
    if (!moved) return;
    if (moved.manual) {
      const nextPinned = [...pinnedCategories];
      const [category] = nextPinned.splice(result.source.index, 1);
      const destinationIndex = Math.min(result.destination.index, nextPinned.length);
      if (destinationIndex !== result.destination.index) {
        animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
      }
      nextPinned.splice(destinationIndex, 0, category);
      onChange(nextPinned);
      return;
    }
    const category = { ...moved };
    delete category.key;
    delete category.manual;
    const nextPinned = [...pinnedCategories];
    const destinationIndex = Math.min(result.destination.index, nextPinned.length);
    if (destinationIndex !== result.destination.index) {
      animateNextChange({ itemKey: moved.key, fromIndex: result.destination.index });
    }
    nextPinned.splice(destinationIndex, 0, category);
    onChange(nextPinned);
  }, [animateNextChange, effectiveItems, onChange, pinnedCategories]);

  const handlePin = useCallback((category) => {
    const key = `${category.id}`;
    animateNextChange();
    if (category.manual) {
      onChange(pinnedCategories.filter((candidate) => `${candidate.id}` !== key));
      return;
    }
    const plainCategory = { ...category };
    delete plainCategory.key;
    delete plainCategory.manual;
    onChange([...pinnedCategories, plainCategory]);
  }, [animateNextChange, onChange, pinnedCategories]);

  const handleHide = useCallback((category) => {
    const key = `${category.id}`;
    const nextManual = pinnedCategories.filter((candidate) => `${candidate.id}` !== key);
    if (nextManual.length !== pinnedCategories.length) onChange(nextManual);
    if (!excludedIds.has(key)) onExcludedChange([...excludedCategoryIds, category.id]);
  }, [excludedCategoryIds, excludedIds, onChange, onExcludedChange, pinnedCategories]);

  const handleRestore = useCallback((id) => {
    onExcludedChange(excludedCategoryIds.filter((candidate) => `${candidate}` !== `${id}`));
  }, [excludedCategoryIds, onExcludedChange]);

  return (
    <div style={S.wrapper}>
      {!hideHeader && <h3>{t('empty.query.categories.editor.title')}</h3>}

      <div style={S.searchArea}>
        <input
          ref={searchRef}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-controls="empty-query-category-suggestions"
          aria-expanded={showDropdown}
          aria-label={t('empty.query.categories.editor.search.placeholder')}
          value={searchInput}
          onChange={handleSearchChange}
          onFocus={() => {
            if (!searchInput.trim()) {
              setSearchResults(initialSuggestions);
              setShowDropdown(initialSuggestions.length > 0);
            } else if (searchResults.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={t('empty.query.categories.editor.search.placeholder')}
          style={S.searchInput}
        />

        {showDropdown && (
          <div id="empty-query-category-suggestions" ref={dropdownRef} role="listbox" style={S.dropdown}>
            {isSearching && <div style={S.dropdownEmpty}>{t('empty.query.categories.editor.searching')}</div>}
            {!isSearching && searchResults.length === 0 && (
              <div style={S.dropdownEmpty}>{t('empty.query.categories.editor.no.results')}</div>
            )}
            {!isSearching && searchResults.map((category) => {
              const alreadyVisible = effectiveItems.some((item) => item.key === `${category.id}`);
              return (
                <div
                  key={category.id}
                  role="option"
                  aria-selected={alreadyVisible}
                  style={{ ...S.dropdownItem, opacity: alreadyVisible ? 0.55 : 1 }}
                  onClick={() => !alreadyVisible && addCategory(category)}
                >
                  <CategoryThumb category={category} small />
                  <div style={S.itemMain}>
                    <div style={S.itemName}>
                      {category.name}
                      <span style={S.categoryId}>#{category.id}</span>
                    </div>
                    {category.path && <div style={S.itemMeta}>{category.path}</div>}
                  </div>
                  <button
                    type="button"
                    disabled={alreadyVisible}
                    style={S.addButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!alreadyVisible) addCategory(category);
                    }}
                  >
                    {alreadyVisible
                      ? t('empty.query.collection.added')
                      : t('empty.query.collection.add')}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={S.toolbar}>
        <span style={S.toolbarSummary}>
          <span style={S.toolbarStrong}>{pinnedCategories.length}</span>
          {' '}{t('empty.query.collection.pinned')}
          {' · '}{effectiveItems.length} {t('empty.query.collection.visible.of', { limit: MAX_VISIBLE_CATEGORIES })}
        </span>
        <span style={S.toolbarActions}>
          <AutoFillControl
            enabled={autoFill}
            limit={MAX_VISIBLE_CATEGORIES}
            unit={t('empty.query.autofill.categories')}
            onChange={onAutoFillChange}
          />
        </span>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="effective-categories">
          {(provided) => (
            <div
              ref={(node) => {
                provided.innerRef(node);
                listMotionRef.current = node;
              }}
              {...provided.droppableProps}
              style={S.zone}
            >
              {effectiveItems.length === 0 && (
                <div style={S.emptyState}>{t('empty.query.categories.editor.generated.empty')}</div>
              )}
              {effectiveItems.map((category, index) => (
                <Draggable
                  key={`category-${category.key}`}
                  draggableId={`category-${category.key}`}
                  index={index}
                >
                  {(drag, snapshot) => (
                    <div
                      ref={drag.innerRef}
                      {...drag.draggableProps}
                      {...drag.dragHandleProps}
                      onMouseEnter={() => setHoveredId(category.key)}
                      onMouseLeave={() => setHoveredId(null)}
                      data-collection-row
                      data-collection-key={category.key}
                      style={{
                        ...S.row,
                        ...drag.draggableProps.style,
                        ...(category.manual ? S.rowPinned : null),
                        ...(hoveredId === category.key ? { background: '#fbfefd' } : null),
                        ...(snapshot?.isDragging ? S.rowDragging : null),
                      }}
                    >
                      {category.manual ? (
                        <span style={S.pinnedPosition}>{index + 1}</span>
                      ) : (
                        <div
                          style={S.dragHandle}
                          aria-label={t('empty.query.categories.editor.drag')}
                        >
                          <DragIndicatorIcon style={S.dragIcon} aria-hidden="true" />
                        </div>
                      )}
                      <CategoryThumb category={category} />
                      <div style={S.itemMain}>
                        <div style={S.itemName}>
                          {category.name}
                          <span style={S.categoryId}>#{category.id}</span>
                        </div>
                        {category.path && <div style={S.itemMeta}>{category.path}</div>}
                      </div>
                      <span style={S.count}>
                        {category.productsCount} {t('empty.query.categories.editor.products.count')}
                      </span>
                      <PinAction
                        pinned={category.manual}
                        label={t(
                          category.manual
                            ? 'empty.query.collection.unpin.item'
                            : 'empty.query.collection.pin.item',
                          { name: category.name },
                        )}
                        onClick={() => handlePin(category)}
                      />
                      <Tooltip title={t('empty.query.collection.block.item', { name: category.name })}>
                        <IconButton
                          size="small"
                          style={{ ...S.actionButton, opacity: hoveredId === category.key ? 1 : 0.55 }}
                          aria-label={t('empty.query.collection.block.item', { name: category.name })}
                          onClick={() => handleHide(category)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {excludedCategoryIds.length > 0 && (
        <div style={S.hiddenZone}>
          <div style={S.hiddenTitle}>{t('empty.query.collection.blocked')}</div>
          {excludedCategoryIds.map((id) => {
            const category = knownCategories.get(`${id}`);
            const name = category?.name || t('empty.query.categories.editor.fallback', { id });
            return (
              <div key={`hidden-category-${id}`} style={S.hiddenMediaRow}>
                <span style={{ ...S.hiddenMedia, display: 'block' }}>
                  <CategoryThumb category={category || { name, image_url: '' }} small />
                </span>
                <span style={S.itemMain}>
                  <span title={name} style={S.hiddenText}>{name}</span>
                  <span style={{ ...S.itemMeta, display: 'block' }}>#{id}</span>
                </span>
                <Tooltip title={t('empty.query.categories.editor.restore.label', { name })}>
                  <IconButton
                    size="small"
                    style={S.actionButton}
                    aria-label={t('empty.query.categories.editor.restore.label', { name })}
                    onClick={() => handleRestore(id)}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoriesEditor;
